import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Canonical lesson release gate (client mirror of the server rule).
 *
 * A lesson is customer-openable only when `lesson_release_status.release_ready`
 * is true — i.e. it passes the shared production-experience contract in
 * `src/lib/curriculum/lessonExperienceContract.ts`. The database enforces the
 * same rule on progress writes, so this hook is UX, not security.
 */
export interface LessonReleaseState {
  loading: boolean;
  ready: boolean;
  deficiencies: string[];
}

export function useLessonReleaseGate(lessonId: string | undefined | null): LessonReleaseState {
  const [state, setState] = useState<LessonReleaseState>({ loading: true, ready: false, deficiencies: [] });

  useEffect(() => {
    let cancelled = false;
    if (!lessonId) {
      setState({ loading: false, ready: false, deficiencies: [] });
      return;
    }
    setState({ loading: true, ready: false, deficiencies: [] });
    (async () => {
      const { data, error } = await supabase
        .from("lesson_release_status")
        .select("release_ready, deficiencies")
        .eq("lesson_id", lessonId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // Fail closed for learners; never silently open an unfinished lesson.
        setState({ loading: false, ready: false, deficiencies: ["release status unavailable"] });
        return;
      }
      setState({
        loading: false,
        ready: Boolean(data?.release_ready),
        deficiencies: (data?.deficiencies as string[] | null) ?? [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  return state;
}

/** Bulk variant for lesson lists: returns the set of release-ready lesson ids. */
export function useReleasedLessonIds(lessonIds: string[]): { loading: boolean; readyIds: Set<string> } {
  const [loading, setLoading] = useState(true);
  const [readyIds, setReadyIds] = useState<Set<string>>(new Set());
  const key = lessonIds.join("|");

  useEffect(() => {
    let cancelled = false;
    if (lessonIds.length === 0) {
      setReadyIds(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("lesson_release_status")
        .select("lesson_id, release_ready")
        .in("lesson_id", lessonIds)
        .eq("release_ready", true);
      if (cancelled) return;
      setReadyIds(new Set((data ?? []).map((r) => r.lesson_id)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { loading, readyIds };
}
